import { useEffect, useMemo, useState } from 'react'
import prefixUID from 'src/feed/ChatMessageSendingSection/messageLocalStoragePrefixUID'
import { DRAFT_MESSAGE_EVENT_NAME } from 'src/feed/ChatMessageSendingSection/useMessage'
import { getItemWithExpirationTime } from 'src/legacy/lib/localStorageWithTTL'

export type DraftMessage = { expiresAt: number; value: string }

const draftEventName = (uid: string) => `${DRAFT_MESSAGE_EVENT_NAME}_${uid}`

const hasDraftMessage = (uid: string) => !!getItemWithExpirationTime(prefixUID(uid))

const splitUids = (joinedUids: string) => (joinedUids === '' ? [] : joinedUids.split(','))

export const useDraftMessage = (uid: string) => {
  const [draftMessage, setDraftMessage] = useState<DraftMessage | null>(getItemWithExpirationTime(prefixUID(uid)))

  useEffect(() => {
    const handle = () => {
      setDraftMessage(getItemWithExpirationTime(prefixUID(uid)))
    }

    window.addEventListener(draftEventName(uid), handle)

    return () => {
      window.removeEventListener(draftEventName(uid), handle)
    }
  }, [uid])

  return draftMessage
}

export const useUidsWithDraftMessage = (uids: Array<string>) => {
  const trackedUids = uids.join(',')
  const [draftingUids, setDraftingUids] = useState(() => splitUids(trackedUids).filter(hasDraftMessage).join(','))

  useEffect(() => {
    const watchedUids = splitUids(trackedUids)
    const handle = () => setDraftingUids(watchedUids.filter(hasDraftMessage).join(','))

    handle()
    watchedUids.forEach((uid) => window.addEventListener(draftEventName(uid), handle))

    return () => {
      watchedUids.forEach((uid) => window.removeEventListener(draftEventName(uid), handle))
    }
  }, [trackedUids])

  return useMemo(() => new Set(splitUids(draftingUids)), [draftingUids])
}
